"use client";

import { useRef, useState } from "react";
import { CalendarCheck2, Clock } from "lucide-react";
import { pushDataLayer } from "../lib/gtm";
import { getAttribution } from "../lib/attribution";
import { isValidEmail } from "../lib/email";
import type { DomainConfig } from "../types";
import TurnstileWidget from "./TurnstileWidget";

export interface AppointmentBlockContent {
  /** Eyebrow boven de hoofdtitel — bv. "Gratis advies" of "3D-ontwerp" */
  eyebrow?: string;
  /** Hoofdtitel boven de form. */
  headline?: string;
  /** Sub-line eronder. */
  subhead?: string;
  /** Bullet-lijstje "wat krijgt u?" — leeg = niet tonen. */
  benefits?: string[];
  /** CTA-label op de submit-knop. */
  cta_submit?: string;
  /** Tekst onder CTA-knop (klein). */
  cta_subnote?: string;
  /** Bevestigingstekst na submit. */
  success_text?: string;
  /** Toont notes-veld (default: ja). */
  show_notes?: boolean;
}

export type AppointmentBlockVariant = "advies-afspraak" | "3d-ontwerp" | "card" | "showroom";

interface Props {
  content: AppointmentBlockContent;
  variant?: AppointmentBlockVariant;
  domain: DomainConfig;
  niche?: string;
  /** Wanneer true: render zonder outer <section>+cream padding, geschikt
   *  voor embed in een modal. Default false (= standalone block). */
  embedded?: boolean;
}

type SlotKey = "morning" | "afternoon" | "evening";
const SLOT_OPTIONS: Array<{ key: SlotKey; label: string; detail: string }> = [
  { key: "morning", label: "Ochtend", detail: "09:00 – 11:00" },
  { key: "afternoon", label: "Middag", detail: "12:00 – 14:00" },
  { key: "evening", label: "Namiddag", detail: "15:00 – 17:00" },
];

function genEventId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function minDateIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1); // morgen
  return d.toISOString().slice(0, 10);
}

function maxDateIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 60);
  return d.toISOString().slice(0, 10);
}

/** Default-content per variant zodat een leeg block niet leeg oogt. */
const VARIANT_DEFAULTS: Record<AppointmentBlockVariant, Required<Omit<AppointmentBlockContent, "show_notes">> & { show_notes: boolean }> = {
  "advies-afspraak": {
    eyebrow: "Gratis · Vrijblijvend",
    headline: "Plan uw persoonlijk adviesgesprek",
    subhead:
      "Onze adviseur belt u op uw voorkeursmoment om uw plannen door te nemen — geen verplichtingen, geen verkooppraatje.",
    benefits: [
      "Gratis telefonisch advies (15-30 min)",
      "Antwoord op uw belangrijkste vragen",
      "Eventueel inplannen van thuisbezoek voor 3D-ontwerp",
    ],
    cta_submit: "Verstuur aanvraag",
    cta_subnote: "Vrijblijvend · Geen kosten · Binnen 24u contact",
    success_text:
      "Onze adviseur neemt op uw voorkeursmoment contact met u op om de afspraak definitief in te plannen.",
    show_notes: false,
  },
  "3d-ontwerp": {
    eyebrow: "Gratis 3D-ontwerp",
    headline: "Plan uw gratis 3D-ontwerp",
    subhead:
      "Onze ontwerper komt vrijblijvend bij u thuis langs, meet uw badkamer in en maakt binnen 5 werkdagen een fotorealistisch 3D-ontwerp op maat — inclusief vaste prijs.",
    benefits: [
      "Inmeet-bezoek bij u thuis (60 min, gratis)",
      "Fotorealistisch 3D-ontwerp van uw nieuwe badkamer",
      "Vaste prijs-offerte erbij — geen verrassingen",
      "Materiaal-stalen meegenomen — voel + zie tegels en kranen",
    ],
    cta_submit: "Plan mijn 3D-ontwerp",
    cta_subnote: "Gratis · Geen verplichtingen · We bellen u op voorkeursmoment",
    success_text:
      "Onze ontwerper neemt op uw voorkeursmoment contact met u op om het inmeet-bezoek in te plannen.",
    show_notes: false,
  },
  card: {
    // Alias for "advies-afspraak" — same defaults, lets BlockLibrary expose
    // a generic card-variant.
    eyebrow: "Gratis · Vrijblijvend",
    headline: "Plan uw afspraak",
    subhead:
      "Vul hieronder uw voorkeursdatum + tijd in. Wij bellen u op die tijd voor een vrijblijvend advies.",
    benefits: [],
    cta_submit: "Verstuur aanvraag",
    cta_subnote: "Vrijblijvend · Binnen 24u contact",
    success_text:
      "Bedankt — onze adviseur neemt op uw voorkeursmoment contact op.",
    show_notes: false,
  },
  showroom: {
    // Toonzaal-bezoek — klant komt naar ons (Zeewolde), geen huisbezoek.
    // Andere intent en sales-flow dan thuisadvies/3D-ontwerp.
    eyebrow: "Gratis · Vrijblijvend",
    headline: "Plan uw bezoek aan onze toonzaal",
    subhead:
      "Kom langs in onze toonzaal in Zeewolde — voel de materialen, bekijk de modellen en stel uw vragen aan onze adviseur. Vrijblijvend, geen verkooppraatje.",
    benefits: [
      "Volledige showroom met alle modellen + materialen",
      "Adviseur reserveert tijd voor u — geen wachten",
      "Gratis koffie + rustig rondkijken kan altijd",
      "Adres: Nijverheidsweg 25, 3899 AD Zeewolde",
    ],
    cta_submit: "Plan mijn bezoek",
    cta_subnote: "Vrijblijvend · We bellen u kort om te bevestigen",
    success_text:
      "Bedankt — wij bellen u op uw voorkeursmoment om een afspraak in onze toonzaal in Zeewolde in te plannen.",
    show_notes: false,
  },
};

/**
 * AppointmentBlock — landing-page versie van de portal's "Verzilver je
 * cadeau"-flow. Klant vult naam + email + telefoon + datum + dagdeel in;
 * sales belt vervolgens op het opgegeven voorkeursmoment.
 *
 * 2 variants:
 *   - "advies-afspraak" : telefonisch adviesgesprek (default)
 *   - "3d-ontwerp"      : gratis thuisbezoek met 3D-ontwerp
 *
 * Submit creëert in 1 call:
 *   1. brochure_leads-row (via lead-API met attributie + lead-magnet tag)
 *   2. brochure_appointments-row gekoppeld aan die lead
 */
export default function AppointmentBlock({
  content,
  variant = "advies-afspraak",
  domain,
  niche,
  embedded = false,
}: Props) {
  const v = VARIANT_DEFAULTS[variant];
  const eyebrow = content.eyebrow ?? v.eyebrow;
  const headline = content.headline ?? v.headline;
  const subhead = content.subhead ?? v.subhead;
  const benefits = content.benefits ?? v.benefits;
  const ctaSubmit = content.cta_submit ?? v.cta_submit;
  const ctaSubnote = content.cta_subnote ?? v.cta_subnote;
  const successText = content.success_text ?? v.success_text;
  const showNotes = content.show_notes ?? v.show_notes;

  // Default Dhr. (zelfde als PostcodeCheckBlock + Hero brochure-form) —
  // minder klikken, klant switcht naar Mevr. indien nodig.
  const [aanhef, setAanhef] = useState<"Dhr." | "Mevr.">("Dhr.");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState<SlotKey | "">("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const captchaTokenRef = useRef<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!lastName.trim()) return setError("Vul uw achternaam in.");
    if (!isValidEmail(email)) return setError("Vul een geldig e-mailadres in.");
    if (!phone.trim()) return setError("Vul uw telefoonnummer in — wij bellen u terug.");
    if (!date) return setError("Kies een voorkeursdatum.");
    if (!slot) return setError("Kies een dagdeel (ochtend, middag of namiddag).");

    setSubmitting(true);
    try {
      const eventId = genEventId();
      const attribution = getAttribution() ?? {};
      const res = await fetch("/api/appointment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: "",
          salutation: aanhef,
          last_name: lastName.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.replace(/\s/g, ""),
          niche: niche ?? "",
          source_url: typeof window !== "undefined" ? window.location.href : "",
          event_id: eventId,
          appointment: {
            preferred_date: date,
            time_slot: slot,
            notes: notes.trim() || null,
            kind: variant,
          },
          ...(captchaTokenRef.current ? { captcha_token: captchaTokenRef.current } : {}),
          ...attribution,
        }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? `HTTP ${res.status}`);
      }
      const apptEvt: import("../lib/gtm").BrochureMicroEvent = {
        event: "appointment_booked",
        page_id: domain.brochure_page_id,
        event_id: eventId,
        appointment_kind: variant,
      };
      // 3D-ontwerp + advies-afspraak + showroom triggeren elk een eigen
      // Google Ads conversion-tag — opt-in via conversion_values config.
      const cv = domain.conversion_values;
      if (variant === "3d-ontwerp" && cv && typeof cv.appointment_3d_value === "number") {
        apptEvt.value = cv.appointment_3d_value;
        apptEvt.currency = cv.currency;
      } else if (variant === "advies-afspraak" && cv && typeof cv.adviesgesprek_value === "number") {
        apptEvt.value = cv.adviesgesprek_value;
        apptEvt.currency = cv.currency;
      } else if (variant === "showroom" && cv && typeof cv.showroom_visit_value === "number") {
        apptEvt.value = cv.showroom_visit_value;
        apptEvt.currency = cv.currency;
      }
      pushDataLayer(apptEvt);
      // Skip ExitIntentPopup na een succesvolle aanvraag — anders krijgt
      // klant verwarrende "claim je cadeau" prompt na submit.
      try { sessionStorage.setItem("bw_exit_intent_seen_v1", "1"); } catch { /* ignore */ }
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  // De inner card-content blijft hetzelfde, alleen de outer wrapper wisselt
  // tussen <section> (standalone) en <div> (embedded in modal).
  const inner = (
      <div
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: embedded ? "0" : "36px 32px",
          background: embedded ? "transparent" : "var(--white, #FFFFFF)",
          borderRadius: embedded ? 0 : 16,
          border: embedded ? "none" : "1px solid var(--border, #E4DED3)",
          boxShadow: embedded ? "none" : "0 4px 16px rgba(20, 28, 40, 0.06)",
        }}
      >
        {!done ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <CalendarCheck2
                size={22}
                strokeWidth={2.2}
                color="var(--terracotta, #FF6B1A)"
                aria-hidden
              />
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "var(--terracotta, #FF6B1A)",
                }}
              >
                {eyebrow}
              </span>
            </div>

            <h2
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: "clamp(24px, 3.2vw, 32px)",
                lineHeight: 1.18,
                color: "var(--ink, #141C28)",
                margin: "0 0 12px",
                letterSpacing: "-0.01em",
              }}
            >
              {headline}
            </h2>
            <p
              style={{
                fontSize: 15,
                lineHeight: 1.55,
                color: "var(--ink-light, #4A5260)",
                margin: "0 0 22px",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              {subhead}
            </p>

            {/* Bullets weglaten in modal-context — popup wordt te lang.
                Standalone-AppointmentBlock op de pagina toont ze wel. */}
            {benefits.length > 0 && !embedded ? (
              <ul
                style={{
                  listStyle: "none",
                  margin: "0 0 28px",
                  padding: 0,
                  display: "grid",
                  gap: 8,
                }}
              >
                {benefits.map((b, i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      fontSize: 14,
                      color: "var(--ink, #141C28)",
                      fontFamily: "system-ui, -apple-system, sans-serif",
                    }}
                  >
                    <span
                      style={{
                        color: "var(--sage, #227A57)",
                        flexShrink: 0,
                        marginTop: 2,
                        fontWeight: 700,
                      }}
                      aria-hidden
                    >
                      ✓
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : null}

            <form onSubmit={submit} style={{ display: "grid", gap: 10 }}>
              <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 10 }}>
                <select
                  required
                  value={aanhef}
                  onChange={(e) => setAanhef(e.target.value as "Dhr." | "Mevr.")}
                  style={{ ...inputStyle, appearance: "auto" }}
                  autoComplete="honorific-prefix"
                  aria-label="Aanhef"
                >
                  <option value="Dhr.">Dhr.</option>
                  <option value="Mevr.">Mevr.</option>
                </select>
                <input
                  type="text"
                  required
                  placeholder="Achternaam"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  style={inputStyle}
                  aria-label="Achternaam"
                />
              </div>
              <input
                type="email"
                placeholder="uwemail@voorbeeld.nl"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                style={inputStyle}
                aria-label="E-mail"
              />
              <input
                type="tel"
                placeholder="06 12 34 56 78"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                style={inputStyle}
                aria-label="Telefoonnummer"
              />

              <label style={{ ...labelStyle, marginTop: 6 }}>Voorkeursdatum</label>
              <input
                type="date"
                min={minDateIso()}
                max={maxDateIso()}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={inputStyle}
                aria-label="Voorkeursdatum"
              />

              <label style={{ ...labelStyle, marginTop: 6 }}>Dagdeel</label>
              {/* Mobile: smallere padding/font op de 3 dagdeel-knoppen zodat
                  '09:00 – 11:00' netjes binnen elke kolom past. */}
              <style>{`
                @media (max-width: 480px) {
                  .bw-slot-btn { padding: 10px 4px !important; }
                  .bw-slot-btn .bw-slot-label { font-size: 13px !important; }
                  .bw-slot-btn .bw-slot-detail { font-size: 10px !important; gap: 2px !important; }
                }
              `}</style>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 8,
                }}
              >
                {SLOT_OPTIONS.map((s) => {
                  const active = slot === s.key;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      className="bw-slot-btn"
                      onClick={() => setSlot(s.key)}
                      style={{
                        padding: "12px 8px",
                        borderRadius: 10,
                        border: `1.5px solid ${active ? "var(--terracotta, #FF6B1A)" : "var(--border, #E4DED3)"}`,
                        background: active ? "#FFF5ED" : "var(--white, #FFFFFF)",
                        cursor: "pointer",
                        textAlign: "center",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        transition: "all 120ms",
                      }}
                    >
                      <div
                        className="bw-slot-label"
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "var(--ink, #141C28)",
                        }}
                      >
                        {s.label}
                      </div>
                      <div
                        className="bw-slot-detail"
                        style={{
                          fontSize: 11,
                          color: "var(--stone, #6F7780)",
                          marginTop: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 3,
                        }}
                      >
                        <Clock size={10} aria-hidden /> {s.detail}
                      </div>
                    </button>
                  );
                })}
              </div>

              {showNotes ? (
                <>
                  <label style={{ ...labelStyle, marginTop: 6 }}>
                    Opmerking{" "}
                    <span style={{ color: "var(--stone, #6F7780)", fontWeight: 400 }}>
                      (optioneel)
                    </span>
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Bv. specifieke wensen of vragen die u alvast wil meegeven."
                    style={{
                      ...inputStyle,
                      resize: "vertical",
                      minHeight: 80,
                      fontFamily: "system-ui, -apple-system, sans-serif",
                    }}
                  />
                </>
              ) : null}

              {error ? (
                <p
                  style={{
                    color: "rgb(220, 38, 38)",
                    fontSize: 13,
                    margin: "6px 0 0",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                  }}
                  role="alert"
                >
                  {error}
                </p>
              ) : null}

              {/* Cloudflare Turnstile — beschermt toonzaal-, advies- en 3D-
                  ontwerp-aanvragen tegen bots. Invisible voor 99% van gebruikers. */}
              <TurnstileWidget tokenRef={captchaTokenRef} />

              <button
                type="submit"
                disabled={submitting}
                style={{
                  marginTop: 10,
                  padding: "14px 24px",
                  borderRadius: 10,
                  background: "var(--terracotta, #FF6B1A)",
                  color: "#FFFFFF",
                  fontSize: 15,
                  fontWeight: 700,
                  border: "none",
                  cursor: submitting ? "wait" : "pointer",
                  fontFamily: "system-ui, -apple-system, sans-serif",
                  opacity: submitting ? 0.6 : 1,
                  boxShadow: "0 2px 6px rgba(255,107,26,0.25)",
                }}
              >
                {submitting ? "Bezig…" : ctaSubmit}
              </button>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--stone, #6F7780)",
                  textAlign: "center",
                  margin: "6px 0 0",
                  fontFamily: "system-ui, -apple-system, sans-serif",
                }}
              >
                {ctaSubnote}
              </p>
            </form>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "var(--sage, #227A57)",
                color: "#FFFFFF",
                display: "grid",
                placeItems: "center",
                fontSize: 32,
                margin: "0 auto 16px",
                fontWeight: 700,
              }}
            >
              ✓
            </div>
            <h2
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: 26,
                color: "var(--ink, #141C28)",
                margin: "0 0 10px",
              }}
            >
              Afspraak aangevraagd!
            </h2>
            <p
              style={{
                fontSize: 15,
                color: "var(--ink-light, #4A5260)",
                lineHeight: 1.55,
                margin: 0,
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              {successText}
            </p>
            <p
              style={{
                fontSize: 13,
                color: "var(--ink-light, #4A5260)",
                lineHeight: 1.5,
                margin: "10px 0 0",
                fontFamily: "system-ui, -apple-system, sans-serif",
                fontStyle: "italic",
                opacity: 0.85,
              }}
            >
              Geen bevestigingsmail ontvangen? Controleer uw spam-/ongewenste mailbox.
            </p>
          </div>
        )}
      </div>
  );

  if (embedded) return inner;

  return (
    <section
      id="appointment-block"
      style={{
        background: "var(--cream, #F6F2EA)",
        padding: "48px 24px",
        scrollMarginTop: 80,
      }}
    >
      {inner}
    </section>
  );
}

const inputStyle: React.CSSProperties = {
  fontSize: 16,
  padding: "13px 16px",
  borderRadius: 10,
  border: "1px solid var(--border, #E4DED3)",
  background: "var(--white, #FFFFFF)",
  color: "var(--ink, #141C28)",
  fontFamily: "system-ui, -apple-system, sans-serif",
  width: "100%",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--ink, #141C28)",
  fontFamily: "system-ui, -apple-system, sans-serif",
};
